<template>
  <div class="user-devices-list">
    <div v-if="loading" class="text-center py-4">
      <b-spinner variant="primary" label="Loading devices..." />
      <p class="mt-2">Loading user devices...</p>
    </div>
    
    <div v-else-if="error" class="alert alert-danger">
      <i class="fas fa-exclamation-triangle mr-2" />
      {{ error }}
    </div>
    
    <div v-else-if="userDevices.length === 0" class="text-center py-4">
      <i class="fas fa-mobile-alt fa-3x text-muted mb-3" />
      <h5>No Devices Found</h5>
      <p class="text-muted">No devices found for this user.</p>
    </div>
    
    <div v-else>
      <!-- Summary -->
      <div class="row mb-4">
        <div class="col-md-4">
          <div class="card border-primary">
            <div class="card-body text-center">
              <i class="fas fa-mobile-alt fa-2x text-primary mb-2" />
              <h4 class="mb-0">{{ userDevices.length }}</h4>
              <small class="text-muted">Total Devices</small>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="card border-success">
            <div class="card-body text-center">
              <i class="fas fa-calendar fa-2x text-success mb-2" />
              <h4 class="mb-0">{{ totalLogins }}</h4>
              <small class="text-muted">Total Logins</small>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="card border-info">
            <div class="card-body text-center">
              <i class="fas fa-clock fa-2x text-info mb-2" />
              <h4 class="mb-0">{{ lastLoginDate }}</h4>
              <small class="text-muted">Last Login</small>
            </div>
          </div>
        </div>
      </div>

      <!-- Devices List -->
      <div class="devices-grid">
        <div
          v-for="(device, index) in userDevices"
          :key="device.id"
          class="device-card mb-3"
        >
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <div>
                <h6 class="mb-0">
                  <i :class="getDeviceIcon(device.device_name)" class="mr-2" />
                  {{ device.device_name }}
                </h6>
                <small class="text-muted">Device #{{ index + 1 }}</small>
              </div>
              <b-badge
                :variant="getDeviceStatusVariant(device)"
                class="px-2 py-1"
              >
                {{ getDeviceStatus(device) }}
              </b-badge>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-6">
                  <h6 class="text-muted mb-2">Device Information</h6>
                  <p class="mb-1">
                    <strong>Fingerprint:</strong>
                    <code class="text-primary">{{ device.fingerprint }}</code>
                  </p>
                  <p class="mb-1">
                    <strong>First Login:</strong>
                    {{ formatDate(device.first_login) }}
                  </p>
                  <p class="mb-1">
                    <strong>Last Login:</strong>
                    {{ formatDate(device.last_login) }}
                  </p>
                  <p class="mb-0">
                    <strong>Login Count:</strong>
                    <span class="badge badge-secondary">{{ device.login_count }}</span>
                  </p>
                </div>
                <div class="col-md-6">
                  <h6 class="text-muted mb-2">User Agent</h6>
                  <div class="user-agent-display">
                    <small class="text-muted">{{ device.user_agent }}</small>
                  </div>
                  
                  <!-- Activity Timeline -->
                  <div class="mt-3">
                    <h6 class="text-muted mb-2">Activity</h6>
                    <div class="activity-bar">
                      <div
                        class="activity-fill"
                        :style="{ width: getActivityPercentage(device) + '%' }"
                      />
                    </div>
                    <small class="text-muted">
                      {{ device.login_count }} logins over {{ getDaysActive(device) }} days
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'UserDevicesList',
  props: {
    userId: {
      type: [String, Number],
      required: true
    },
    userEmail: {
      type: String,
      default: ''
    }
  },
  computed: {
    ...mapGetters('deviceTracking', [
      'userDevices',
      'loading',
      'error'
    ]),
    totalLogins() {
      return this.userDevices.reduce((total, device) => total + device.login_count, 0);
    },
    lastLoginDate() {
      if (this.userDevices.length === 0) return 'N/A';
      
      const latestLogin = this.userDevices.reduce((latest, device) => {
        const deviceDate = new Date(device.last_login);
        return deviceDate > latest ? deviceDate : latest;
      }, new Date(0));
      
      return this.formatDate(latestLogin.toISOString());
    }
  },
  methods: {
    ...mapActions('deviceTracking', ['fetchUserDevices']),
    
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) {
        return 'Today, ' + date.toLocaleTimeString();
      } else if (diffDays === 1) {
        return 'Yesterday, ' + date.toLocaleTimeString();
      } else if (diffDays < 7) {
        return `${diffDays} days ago`;
      } else {
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
      }
    },
    
    getDeviceIcon(deviceName) {
      const name = deviceName.toLowerCase();
      if (name.includes('iphone')) return 'fab fa-apple';
      if (name.includes('android') || name.includes('mobile')) return 'fab fa-android';
      if (name.includes('ipad') || name.includes('tablet')) return 'fas fa-tablet-alt';
      if (name.includes('chrome')) return 'fab fa-chrome';
      if (name.includes('firefox')) return 'fab fa-firefox';
      if (name.includes('safari')) return 'fab fa-safari';
      if (name.includes('desktop')) return 'fas fa-desktop';
      return 'fas fa-mobile-alt';
    },
    
    getDeviceStatus(device) {
      const lastLogin = new Date(device.last_login);
      const now = new Date();
      const diffHours = (now - lastLogin) / (1000 * 60 * 60);
      
      if (diffHours < 24) return 'Active';
      if (diffHours < 168) return 'Recent'; // 7 days
      return 'Inactive';
    },
    
    getDeviceStatusVariant(device) {
      const status = this.getDeviceStatus(device);
      switch (status) {
        case 'Active': return 'success';
        case 'Recent': return 'warning';
        case 'Inactive': return 'secondary';
        default: return 'secondary';
      }
    },
    
    getDaysActive(device) {
      const firstLogin = new Date(device.first_login);
      const lastLogin = new Date(device.last_login);
      const diffMs = lastLogin - firstLogin;
      const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
      return Math.max(1, diffDays);
    },
    
    getActivityPercentage(device) {
      const maxLogins = Math.max(...this.userDevices.map(d => d.login_count));
      return maxLogins > 0 ? (device.login_count / maxLogins) * 100 : 0;
    }
  },
  
  async created() {
    if (this.userId) {
      await this.fetchUserDevices(this.userId);
    }
  }
};
</script>

<style scoped>
.user-devices-list {
  max-height: 70vh;
  overflow-y: auto;
}

.device-card .card {
  transition: transform 0.2s ease-in-out;
}

.device-card .card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.user-agent-display {
  background-color: #f8f9fa;
  padding: 0.5rem;
  border-radius: 4px;
  border-left: 3px solid #007bff;
  word-break: break-all;
  font-family: monospace;
  font-size: 0.8rem;
  max-height: 60px;
  overflow-y: auto;
}

.activity-bar {
  height: 8px;
  background-color: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.25rem;
}

.activity-fill {
  height: 100%;
  background: linear-gradient(90deg, #28a745, #20c997);
  transition: width 0.3s ease;
}

.card-header {
  background-color: #f8f9fc;
  border-bottom: 1px solid #e3e6f0;
}

.badge {
  font-size: 0.75rem;
}

code {
  font-size: 0.8rem;
  padding: 0.2rem 0.4rem;
  border-radius: 3px;
}

@media (max-width: 768px) {
  .devices-grid .col-md-6 {
    margin-bottom: 1rem;
  }
  
  .user-agent-display {
    font-size: 0.7rem;
  }
}
</style> 